import React from 'react';
import { Building2, Fuel, MapPin, Gauge, Calendar } from 'lucide-react';
import { AddressRecord } from '../../lib/supabaseClient';
import { CompetitorWithDistance } from '../../services/competitorQueryService';

interface CompetitorComparisonTableProps {
  subjectProperty: {
    address: string;
    buildingSize?: number;
    canopySize?: number;
    lotSize?: number;
    brand?: string;
    yearBuilt?: number;
    numberOfPumps?: number;
    carWashSize?: number;
    serviceBays?: number;
    ustCount?: number;
  };
  competitors: CompetitorWithDistance[];
  radiusMiles: number;
}

export function CompetitorComparisonTable({
  subjectProperty,
  competitors,
  radiusMiles
}: CompetitorComparisonTableProps) {
  const formatNumber = (num: number | undefined) => {
    if (!num || num === 0) return '—';
    return num.toLocaleString();
  };

  const formatDistance = (miles: number | undefined) => {
    if (!miles) return '—';
    return `${miles.toFixed(2)} mi`;
  };

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center gap-2 text-blue-600 mb-1">
            <MapPin className="w-4 h-4" />
            <span className="text-sm font-medium">Total Found</span>
          </div>
          <p className="text-2xl font-bold text-blue-900">{competitors.length}</p>
          <p className="text-xs text-blue-600 mt-1">within {radiusMiles} mile{radiusMiles !== 1 ? 's' : ''}</p>
        </div>

        <div className="bg-purple-50 rounded-lg p-4">
          <div className="flex items-center gap-2 text-purple-600 mb-1">
            <Fuel className="w-4 h-4" />
            <span className="text-sm font-medium">Branded</span>
          </div>
          <p className="text-2xl font-bold text-purple-900">
            {competitors.filter(c => c.Fuel_Brand && c.Fuel_Brand.toLowerCase() !== 'unbranded').length}
          </p>
          <p className="text-xs text-purple-600 mt-1">major brands</p>
        </div>

        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center gap-2 text-green-600 mb-1">
            <Building2 className="w-4 h-4" />
            <span className="text-sm font-medium">Avg Building</span>
          </div>
          <p className="text-2xl font-bold text-green-900">
            {competitors.filter(c => c.Cstore_Size_Measured && c.Cstore_Size_Measured > 0).length > 0
              ? Math.round(
                  competitors
                    .filter(c => c.Cstore_Size_Measured && c.Cstore_Size_Measured > 0)
                    .reduce((sum, c) => sum + (c.Cstore_Size_Measured || 0), 0) /
                    competitors.filter(c => c.Cstore_Size_Measured && c.Cstore_Size_Measured > 0).length
                ).toLocaleString()
              : '—'
            } sf</p>
          <p className="text-xs text-green-600 mt-1">store size</p>
        </div>

        <div className="bg-orange-50 rounded-lg p-4">
          <div className="flex items-center gap-2 text-orange-600 mb-1">
            <Gauge className="w-4 h-4" />
            <span className="text-sm font-medium">Closest</span>
          </div>
          <p className="text-2xl font-bold text-orange-900">
            {competitors.length > 0 ? `${competitors[0].distance_miles?.toFixed(2)} mi` : '—'}
          </p>
          <p className="text-xs text-orange-600 mt-1">competitor</p>
        </div>
      </div>

      {/* Subject Property Header */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-500 rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          <h3 className="text-lg font-bold text-green-900">YOUR PROPERTY (Subject)</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 text-sm">
          <div>
            <p className="text-gray-600 font-medium mb-1">Address</p>
            <p className="text-gray-900 font-semibold">{subjectProperty.address}</p>
          </div>
          <div>
            <p className="text-gray-600 font-medium mb-1">Building Size</p>
            <p className="text-gray-900 font-semibold">{formatNumber(subjectProperty.buildingSize)} sf</p>
          </div>
          <div>
            <p className="text-gray-600 font-medium mb-1">Canopy Size</p>
            <p className="text-gray-900 font-semibold">{formatNumber(subjectProperty.canopySize)} sf</p>
          </div>
          <div>
            <p className="text-gray-600 font-medium mb-1">Brand</p>
            <p className="text-gray-900 font-semibold">{subjectProperty.brand || 'Unbranded'}</p>
          </div>
          <div>
            <p className="text-gray-600 font-medium mb-1">Year Built</p>
            <p className="text-gray-900 font-semibold">{subjectProperty.yearBuilt || '—'}</p>
          </div>
          <div>
            <p className="text-gray-600 font-medium mb-1">Service Bays</p>
            <p className="text-gray-900 font-semibold">{subjectProperty.serviceBays || 0}</p>
          </div>
        </div>
      </div>

      {/* Competitors Table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 border-b px-4 py-3">
          <h3 className="text-lg font-semibold text-gray-900">
            Competitors ({competitors.length})
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Sorted by distance • Within {radiusMiles} mile radius
          </p>
        </div>

        {competitors.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <MapPin className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-lg font-medium">No competitors found</p>
            <p className="text-sm mt-1">Try increasing the search radius</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100 text-gray-700 text-xs font-semibold uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">#</th>
                  <th className="px-4 py-3 text-left">Address</th>
                  <th className="px-4 py-3 text-right">Distance</th>
                  <th className="px-4 py-3 text-left">Brand</th>
                  <th className="px-4 py-3 text-right">Building (sf)</th>
                  <th className="px-4 py-3 text-right">Canopy (sf)</th>
                  <th className="px-4 py-3 text-right">Lot (sf)</th>
                  <th className="px-4 py-3 text-center">Year Built</th>
                  <th className="px-4 py-3 text-center">Bays</th>
                  <th className="px-4 py-3 text-center">USTs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {competitors.map((competitor, index) => (
                  <tr
                    key={competitor.OBJECTID}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">{competitor.Facility_Address}</p>
                      <p className="text-xs text-gray-500">{competitor.City}, {competitor.STATE}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-blue-600">
                      {formatDistance(competitor.distance_miles)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {competitor.Fuel_Brand ? (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          competitor.Fuel_Brand.toLowerCase() === 'unbranded'
                            ? 'bg-gray-200 text-gray-700'
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {competitor.Fuel_Brand}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900">
                      {formatNumber(competitor.Cstore_Size_Measured)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900">
                      {formatNumber(competitor.Fueling_Canopy_Size_Measured)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900">
                      {formatNumber(competitor.Lot_Size)}
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-gray-900">
                      {competitor.Year_Built_Public_Record || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-gray-900">
                      {competitor.Service_Bay_Count || 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-gray-900">
                      {competitor.UST_Count || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pricing Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Fuel className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900">Live Pricing Data</p>
            <p className="text-xs text-blue-700 mt-1">
              Competitive pricing analysis coming in Phase 2. We're working on integrating live fuel price data to provide comprehensive market insights.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
